/**
 * WHAT CONDITION A PART IS IN, IN THE WORDS THE LAW USES.
 *
 * California Code of Regulations title 16 section 3356 requires an invoice
 * to state, for each part, "whether the part was new, used, reconditioned,
 * rebuilt, an OEM crash part, or a non-OEM aftermarket crash part."
 * Business and Professions Code 9884.8 adds the mixed case: where a
 * component system is made of both new and used, rebuilt or reconditioned
 * parts, the invoice must say so.
 *
 * PURE ON PURPOSE, like trade.ts. The server says these words too. The
 * estimate email, the invoice email and the customer portal all render from
 * here, so there is one spelling of "Non-OEM aftermarket crash part" in the
 * whole system rather than three that drift.
 *
 * THE LABELS ARE THE STATUTE'S WORDS AND ARE NOT UP FOR EDITING. BAR has
 * said in writing that near misses like "Opt-OEM" do not satisfy the
 * requirement, so a shop's preferred shorthand is not a safe substitute.
 */

export const PART_CONDITIONS = [
  "new",
  "used",
  "reconditioned",
  "rebuilt",
  "oem_crash",
  "aftermarket_crash",
  "new_and_used",
] as const;

export type PartCondition = (typeof PART_CONDITIONS)[number];

type Entry = {
  /** What the customer reads on the estimate and the invoice. */
  label: string;
  /** The short form for a dense table or a chip. */
  short: string;
  /** What the advisor picks in the editor. */
  option: string;
};

const WORDS: Record<PartCondition, Entry> = {
  new: {
    label: "New",
    short: "New",
    option: "New",
  },
  used: {
    label: "Used",
    short: "Used",
    option: "Used",
  },
  reconditioned: {
    label: "Reconditioned",
    short: "Recon",
    option: "Reconditioned",
  },
  rebuilt: {
    label: "Rebuilt",
    short: "Rebuilt",
    option: "Rebuilt",
  },
  oem_crash: {
    label: "OEM crash part",
    short: "OEM crash",
    option: "OEM crash part",
  },
  aftermarket_crash: {
    label: "Non-OEM aftermarket crash part",
    short: "Non-OEM crash",
    option: "Non-OEM aftermarket crash part",
  },
  new_and_used: {
    label: "Contains both new and used parts",
    short: "New and used",
    option: "Mixed, new and used together",
  },
};

/** Narrow an unknown database value to a condition we have words for. */
export function asCondition(v: unknown): PartCondition | null {
  const s = String(v ?? "").trim().toLowerCase();
  return (PART_CONDITIONS as readonly string[]).includes(s)
    ? (s as PartCondition)
    : null;
}

/**
 * The customer facing phrase, or null when nobody has said yet.
 *
 * NULL IS NOT "NEW". A part with no condition on it prints nothing rather
 * than guessing, because a wrong statement here is the violation itself.
 */
export function conditionLabel(v: unknown): string | null {
  const c = asCondition(v);
  return c ? WORDS[c].label : null;
}

export function conditionShort(v: unknown): string | null {
  const c = asCondition(v);
  return c ? WORDS[c].short : null;
}

/** Options for a <select>, in the order an advisor reaches for them. */
export function conditionOptions(): Array<{ value: PartCondition; text: string }> {
  return PART_CONDITIONS.map((v) => ({ value: v, text: WORDS[v].option }));
}

/**
 * How a part line reads once the condition is on it.
 *
 *   describePart("Alternator", "rebuilt") -> "Alternator (Rebuilt)"
 *
 * The condition goes in brackets AFTER the description rather than being
 * mixed into it, so it stays machine readable and so an advisor cannot half
 * delete it while retyping the description.
 */
export function describePart(description: string, v: unknown): string {
  const label = conditionLabel(v);
  const d = String(description ?? "").trim();
  if (!label) return d;
  // Do not say it twice when the description already spells it out, which
  // is how every part on the book before this column existed was written.
  if (d.toLowerCase().includes(label.toLowerCase())) return d;
  return `${d} (${label})`;
}

/**
 * Which part lines on a job still have nothing said about them.
 *
 * This is what the invoice screen warns on. It is deliberately a list and
 * not a boolean, because "three parts need a condition" is actionable and
 * "this job is not compliant" is not.
 */
export function partsMissingCondition<
  T extends { kind?: string | null; description?: string | null; part_condition?: unknown },
>(lines: readonly T[]): T[] {
  return lines.filter(
    (l) => String(l.kind ?? "").toLowerCase() === "part" && asCondition(l.part_condition) === null,
  );
}
